/*
 * Moonchild — global UI behaviors
 * Single source of truth for reveal-on-scroll. Replaces 25+ duplicated
 * IntersectionObserver implementations across sections.
 */

(function () {
  'use strict';

  var revealObserver = null;
  var prefersReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    initReveals(document);
    initCounters(document);
    initBuyQuantity(document);
    initPurchaseOptions(document);
    initProblemInfographics(document);
    initPdpReviews(document);
    initPdpAnalytics(document);
    window.setTimeout(revealHashTarget, 120);

    // Re-scan on Shopify theme editor section reload
    document.addEventListener('shopify:section:load', rescan);
    document.addEventListener('shopify:section:reorder', rescan);
    window.addEventListener('hashchange', revealHashTarget);
  }

  function rescan(event) {
    var scope = event && event.target ? event.target : document;
    initReveals(scope);
    initCounters(scope);
    initBuyQuantity(scope);
    initPurchaseOptions(scope);
    initProblemInfographics(scope);
    initPdpReviews(scope);
    initPdpAnalytics(scope);
  }

  function trackPdpEvent(name, detail) {
    detail = detail || {};

    window.dispatchEvent(new CustomEvent(name, { detail: detail }));

    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: name }, detail));
    }

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try {
        window.Shopify.analytics.publish(name, detail);
      } catch (err) {
        // Shopify's analytics bridge may reject custom events in some previews.
      }
    }
  }

  function initReveals(scope) {
    scope = scope || document;
    prepareRevealGroups(scope);

    var items = scope.querySelectorAll('.mc-reveal:not(.is-visible)');
    if (!items.length) return;

    if (prefersReduce || !('IntersectionObserver' in window)) {
      items.forEach(revealElement);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            revealElement(entry.target);
            revealObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -48px 0px'
      });
    }

    items.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  function prepareRevealGroups(scope) {
    scope.querySelectorAll('[data-mc-reveal-group]').forEach(function (group) {
      group.querySelectorAll('.mc-reveal').forEach(function (el, index) {
        if (!el.style.getPropertyValue('--mc-reveal-index')) {
          el.style.setProperty('--mc-reveal-index', index);
        }
      });
    });
  }

  function revealElement(el) {
    el.classList.add('is-visible');
  }

  function revealHashTarget() {
    if (!window.location.hash || window.location.hash.length < 2) return;
    var id = window.location.hash.slice(1);
    var target;

    try {
      target = document.getElementById(decodeURIComponent(id));
    } catch (err) {
      target = document.getElementById(id);
    }

    if (!target) return;
    if (target.classList && target.classList.contains('mc-reveal')) {
      revealElement(target);
    }
    target.querySelectorAll('.mc-reveal').forEach(revealElement);
  }

  function initPurchaseOptions(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-mc-purchase-options]').forEach(function (root) {
      if (root.dataset.mcPurchaseOptionsReady === 'true') return;
      root.dataset.mcPurchaseOptionsReady = 'true';

      var hiddenInput = root.querySelector('[data-mc-selling-plan-input]');
      var fallback = root.querySelector('[data-mc-selling-plan-fallback]');
      var rechargeMount = root.querySelector('[data-mc-recharge-widget]');

      function syncSelectedPlan() {
        if (!hiddenInput) return;
        var checked = root.querySelector('input[name="mc_purchase_option"]:checked');
        if (checked) {
          hiddenInput.value = checked.dataset.sellingPlanId || '';
        }
      }

      function syncRechargeMount() {
        if (!fallback || !hiddenInput || !rechargeMount) return;
        var hasRechargeWidget = rechargeMount.children.length > 0 || rechargeMount.textContent.trim().length > 0;
        fallback.hidden = hasRechargeWidget;
        hiddenInput.disabled = hasRechargeWidget;
      }

      root.addEventListener('change', function (event) {
        if (event.target && event.target.name === 'mc_purchase_option') {
          syncSelectedPlan();
        }
      });

      syncSelectedPlan();
      syncRechargeMount();

      if (rechargeMount && 'MutationObserver' in window) {
        var observer = new MutationObserver(syncRechargeMount);
        observer.observe(rechargeMount, { childList: true, subtree: true, characterData: true });
      }
    });
  }

  function initBuyQuantity(scope) {
    scope = scope || document;
    var BUNDLE_FALLBACK_VARIANT_ID = '50209496596794';
    scope.querySelectorAll('[data-mc-buy-quantity]').forEach(function (root) {
      if (root.dataset.mcBuyQuantityReady === 'true') return;
      root.dataset.mcBuyQuantityReady = 'true';

      var input = root.querySelector('[data-mc-quantity-input]');
      var bundleVariantInput = root.querySelector('[data-mc-bundle-variant-input]');
      var buttons = root.querySelectorAll('[data-mc-quantity-value]');
      if (!input || !buttons.length) return;

      var form = root.closest('form');
      // Rendered outside this root (below "More payment options"), so resolve at form scope.
      var tryOnceButton = (form || root).querySelector('[data-mc-try-once]');
      var productForm = root.closest('product-form');
      var variantInput = form ? form.querySelector('[data-mc-primary-variant-input]') : null;
      var submitButton = form ? form.querySelector('[type="submit"][name="add"]') : null;
      var submitText = submitButton ? submitButton.querySelector('span') : null;
      var purchaseOptions = form ? form.querySelector('[data-mc-purchase-options]') : null;
      var sellingPlanInput = form ? form.querySelector('[data-mc-selling-plan-input]') : null;
      var stickyBar = document.querySelector('[data-mc-sticky-buy-bar]');
      var stickyPrice = stickyBar ? stickyBar.querySelector('.mc-sbb__price strong') : null;
      var stickyCompare = stickyBar ? stickyBar.querySelector('.mc-sbb__price del') : null;
      var stickyCta = stickyBar ? stickyBar.querySelector('.mc-sbb__cta') : null;

      function getSelectedButton() {
        return root.querySelector('[data-mc-quantity-value].is-active') || buttons[0];
      }

      function getSelectedVariantId(button) {
        return (button && button.dataset.mcVariantId) || (bundleVariantInput && bundleVariantInput.value) || BUNDLE_FALLBACK_VARIANT_ID;
      }

      function syncVariantInput(button) {
        var variantId = getSelectedVariantId(button);
        if (bundleVariantInput) bundleVariantInput.value = variantId;
        if (variantInput) variantInput.value = variantId;
      }

      function syncSellingPlan(button, offerType) {
        if (!sellingPlanInput) return;

        if (offerType === 'one-time') {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
          return;
        }

        var sellingPlanId = button ? button.dataset.mcSellingPlanId || '' : '';
        sellingPlanInput.value = sellingPlanId;
        sellingPlanInput.disabled = sellingPlanId === '';
      }

      function setText(selector, text) {
        if (!purchaseOptions) return;
        purchaseOptions.querySelectorAll(selector).forEach(function (el) {
          el.textContent = text;
        });
      }

      function syncBundlePrices() {
        buttons.forEach(function (button) {
          var price = button.dataset.mcBundleSubPrice;
          var compare = button.dataset.mcBundleSubCompare;
          var perNight = button.dataset.mcBundleSubNight;
          var save = button.dataset.mcBundleSubSave;
          var cardPrice = button.querySelector('[data-mc-bundle-card-price]');
          var cardCompare = button.querySelector('[data-mc-bundle-card-compare]');
          var cardNight = button.querySelector('[data-mc-bundle-card-night]');
          var cardSave = button.querySelector('[data-mc-bundle-card-save]');
          if (cardPrice && price) cardPrice.textContent = price;
          if (cardCompare && compare) cardCompare.textContent = compare;
          if (cardNight && perNight) cardNight.textContent = perNight;
          if (cardSave && save) cardSave.textContent = save;
        });
      }

      function syncPurchaseCopy() {
        var selected = getSelectedButton();
        var offerType = root.dataset.mcOfferType === 'one-time' ? 'one-time' : 'subscribe';
        var priceSource = offerType === 'one-time' ? buttons[0] : selected;
        var subPrice = priceSource.dataset.mcBundleSubPrice || '';
        var onePrice = priceSource.dataset.mcBundleOnePrice || '';
        var subCompare = priceSource.dataset.mcBundleSubCompare || onePrice;
        var oneCompare = priceSource.dataset.mcBundleOneCompare || '';
        var subNight = priceSource.dataset.mcBundleSubNight || '';
        var oneNight = priceSource.dataset.mcBundleOneNight || '';
        var supply = priceSource.dataset.mcBundleNights || '';
        var supplyLabel = supply.replace(/\s+nights?$/, '-night');
        var activePrice = offerType === 'one-time' ? onePrice : subPrice;
        var activeCompare = offerType === 'one-time' ? oneCompare : subCompare;

        setText('[data-mc-offer-price="subscribe"]', subPrice);
        setText('[data-mc-offer-night="subscribe"]', subNight);
        setText('[data-mc-offer-price="one-time"]', onePrice);
        setText('[data-mc-offer-night="one-time"]', oneNight);
        setText('[data-mc-offer-compare]', subCompare);
        setText('[data-mc-offer-compare-one]', oneCompare);
        setText('[data-mc-offer-supply]', supplyLabel ? supplyLabel + ' supply' : '');
        setText('[data-mc-offer-supply-one]', supplyLabel ? supplyLabel + ' supply delivered once.' : '');

        syncVariantInput(priceSource);
        syncSellingPlan(priceSource, offerType);

        if (submitText) {
          submitText.textContent = 'Start tonight';
        }

        if (stickyPrice && activePrice) {
          stickyPrice.textContent = activePrice;
        }

        if (stickyCompare && activeCompare) {
          stickyCompare.textContent = activeCompare;
        }

        if (stickyCta) {
          stickyCta.textContent = 'Start tonight';
        }

        if (productForm) {
          productForm.dataset.mcSelectedBundlePrice = activePrice;
          productForm.dataset.mcSelectedBundleQuantity = priceSource.dataset.mcQuantityValue || input.value || '1';
          productForm.dataset.mcSelectedOfferType = offerType;
        }
      }

      function syncAll() {
        syncBundlePrices();
        syncPurchaseCopy();
      }

      function selectQuantity(value, shouldTrack) {
        root.dataset.mcOfferType = 'subscribe';
        root.classList.remove('is-try-once');
        if (tryOnceButton) {
          tryOnceButton.classList.remove('is-active');
          tryOnceButton.setAttribute('aria-pressed', 'false');
        }
        var activeButton = null;
        buttons.forEach(function (button) {
          var active = button.dataset.mcQuantityValue === value;
          if (active) activeButton = button;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        // `value` is the bundle size (1/3/6) and identifies the card. It is NOT
        // the cart quantity: each bundle is its own variant, so the line is
        // quantity 1. Sending the bundle size here would order 3x a 3-jar SKU.
        input.value = (activeButton && activeButton.dataset.mcCartQuantity) || '1';
        syncAll();

        if (shouldTrack) {
          trackPdpEvent('pdp_bundle_selected', {
            bundle_size: value,
            offer_type: 'subscribe'
          });
        }
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          selectQuantity(button.dataset.mcQuantityValue || '1', true);
        });
      });

      if (tryOnceButton) {
        tryOnceButton.setAttribute('aria-pressed', 'false');
        tryOnceButton.addEventListener('click', function () {
          root.dataset.mcOfferType = 'one-time';
          root.classList.add('is-try-once');
          input.value = '1';
          tryOnceButton.classList.add('is-active');
          tryOnceButton.setAttribute('aria-pressed', 'true');
          buttons.forEach(function (button) {
            button.classList.remove('is-active');
            button.setAttribute('aria-pressed', 'false');
          });
          syncAll();
          trackPdpEvent('pdp_bundle_selected', {
            bundle_size: '1',
            offer_type: 'one-time'
          });

          // Add to cart straight away, exactly like "Start tonight". syncAll() has
          // already written the variant, set quantity to 1 and disabled the selling
          // plan, so clicking submit posts a one-time line item. product-form.js
          // listens for the form's submit event and opens the cart from there.
          if (submitButton && !submitButton.disabled && submitButton.getAttribute('aria-disabled') !== 'true') {
            submitButton.click();
          }
        });
      }

      if (purchaseOptions) {
        purchaseOptions.addEventListener('change', function (event) {
          if (event.target && event.target.name === 'mc_purchase_option') {
            syncAll();
          }
        });
      }

      syncVariantInput(getSelectedButton());
      // Default bundle comes from whichever card ships marked is-active — the
      // quantity field can no longer carry it, since that is always 1 now.
      selectQuantity(getSelectedButton().dataset.mcQuantityValue || '1', false);
    });
  }

  function initProblemInfographics(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-mc-problem-section]').forEach(function (root) {
      if (root.dataset.mcProblemReady === 'true') return;
      root.dataset.mcProblemReady = 'true';

      var triggers = root.querySelectorAll('[data-mc-problem-trigger]');
      var panels = root.querySelectorAll('[data-mc-problem-panel]');
      var tabs = root.querySelectorAll('[data-mc-problem-tab]');
      if (!triggers.length || !panels.length) return;

      function selectProblem(key) {
        root.dataset.mcProblemActive = key;

        triggers.forEach(function (trigger) {
          var active = trigger.dataset.mcProblemTrigger === key;
          trigger.classList.toggle('is-active', active);
          trigger.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        panels.forEach(function (panel) {
          var active = panel.dataset.mcProblemPanel === key;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        tabs.forEach(function (tab) {
          tab.classList.toggle('is-active', tab.dataset.mcProblemTab === key);
        });
      }

      triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          selectProblem(trigger.dataset.mcProblemTrigger || 'moonchild');
        });
      });

      var checked = root.querySelector('[data-mc-problem-trigger].is-active');
      selectProblem((checked && checked.dataset.mcProblemTrigger) || triggers[0].dataset.mcProblemTrigger || 'moonchild');
    });
  }

  function initPdpReviews(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-mc-pdp-reviews]').forEach(function (root) {
      if (root.dataset.mcReviewsReady === 'true') return;
      root.dataset.mcReviewsReady = 'true';

      var filters = root.querySelectorAll('[data-mc-review-filter]');
      var items = root.querySelectorAll('[data-mc-review-item]');
      var rails = root.querySelectorAll('.mc-pdp-reviews__quotes, .mc-pdp-reviews__media');
      if (!filters.length || !items.length) return;

      filters.forEach(function (filter) {
        filter.addEventListener('click', function () {
          var active = filter.dataset.mcReviewFilter || 'all';

          filters.forEach(function (button) {
            button.setAttribute('aria-pressed', button === filter ? 'true' : 'false');
          });

          items.forEach(function (item) {
            var show = active === 'all' || item.dataset.category === active;
            item.classList.toggle('mc-pdp-reviews__empty', !show);
          });

          rails.forEach(function (rail) {
            rail.scrollLeft = 0;
          });

          trackPdpEvent('pdp_review_filter_clicked', {
            filter: active
          });
        });
      });
    });
  }

  function initPdpAnalytics(scope) {
    scope = scope || document;
    initPdpSectionTracking(scope);
    initPdpFaqTracking(scope);
    initPdpScrollCtas(scope);
  }

  function initPdpSectionTracking(scope) {
    var sections = scope.querySelectorAll('section[id]:not([id^="shopify-section-"]), #buy-box');
    if (!sections.length) return;

    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (section) {
        if (section.dataset.mcSectionTracked === 'true') return;
        section.dataset.mcSectionTracked = 'true';
        trackPdpEvent('pdp_section_in_view', { section_name: section.id || 'buy-box' });
      });
      return;
    }

    if (!window.mcPdpSectionObserver) {
      window.mcPdpSectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.target.dataset.mcSectionTracked === 'true') return;
          entry.target.dataset.mcSectionTracked = 'true';
          window.mcPdpSectionObserver.unobserve(entry.target);
          trackPdpEvent('pdp_section_in_view', {
            section_name: entry.target.id || 'buy-box'
          });
        });
      }, {
        threshold: 0.3,
        rootMargin: '0px 0px -20% 0px'
      });
    }

    sections.forEach(function (section) {
      if (section.dataset.mcSectionTrackingReady === 'true' || section.dataset.mcSectionTracked === 'true') return;
      section.dataset.mcSectionTrackingReady = 'true';
      window.mcPdpSectionObserver.observe(section);
    });
  }

  function initPdpFaqTracking(scope) {
    scope.querySelectorAll('.mc-pdp-faq__item').forEach(function (item, index) {
      if (item.dataset.mcFaqTrackingReady === 'true') return;
      item.dataset.mcFaqTrackingReady = 'true';

      item.addEventListener('toggle', function () {
        if (!item.open) return;
        var question = item.querySelector('.mc-pdp-faq__summary span');
        trackPdpEvent('pdp_faq_opened', {
          question_id: 'q' + (index + 1),
          question: question ? question.textContent.trim() : ''
        });
      });
    });
  }

  function initPdpScrollCtas(scope) {
    scope.querySelectorAll('[data-mc-scroll-cta], [data-mc-scroll-to-buy-box]').forEach(function (cta) {
      if (cta.dataset.mcScrollTrackingReady === 'true') return;
      cta.dataset.mcScrollTrackingReady = 'true';

      cta.addEventListener('click', function () {
        var position = cta.dataset.mcCtaPosition || 'unknown';
        if (cta.closest('[data-mc-sticky-buy-bar]')) position = 'sticky-mobile';
        trackPdpEvent('pdp_scroll_anchor_cta_clicked', {
          cta_position: position,
          href: cta.getAttribute('href') || '#buy-box'
        });
      });
    });
  }

  /* Stat counter animation —
     <span data-mc-counter data-target="23" data-prefix="+" data-suffix=" min">0</span>
     Animates from 0 to data-target over ~1200ms when in view. */
  function initCounters(scope) {
    scope = scope || document;
    if (!('IntersectionObserver' in window)) {
      scope.querySelectorAll('[data-mc-counter]').forEach(function (el) {
        renderCounter(el, parseFloat(el.dataset.target));
      });
      return;
    }
    var prefersReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var target = parseFloat(el.dataset.target);
          if (prefersReduce) {
            renderCounter(el, target);
          } else {
            animateCounter(el, target);
          }
          io.unobserve(el);
        }
      });
    }, { threshold: 0.4 });
    scope.querySelectorAll('[data-mc-counter]').forEach(function (el) { io.observe(el); });
  }

  function renderCounter(el, value) {
    var prefix = el.dataset.prefix || '';
    var suffix = el.dataset.suffix || '';
    var decimals = parseInt(el.dataset.decimals || '0', 10);
    el.textContent = prefix + value.toFixed(decimals) + suffix;
  }

  function animateCounter(el, target) {
    var prefix = el.dataset.prefix || '';
    var suffix = el.dataset.suffix || '';
    var decimals = parseInt(el.dataset.decimals || '0', 10);
    var duration = parseInt(el.dataset.duration || '1400', 10);
    var startTime = null;

    function step(t) {
      if (!startTime) startTime = t;
      var elapsed = t - startTime;
      var progress = Math.min(1, elapsed / duration);
      // easeOutCubic
      var eased = 1 - Math.pow(1 - progress, 3);
      var value = target * eased;
      el.textContent = prefix + value.toFixed(decimals) + suffix;
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
